import type { AttendeeConferenceReviewValues, AttendeeLearningRecordValues } from "@verity-connect/contracts";
import { useState } from "react";

/**
 * What she can do with a document once she has read it. It sits under the document, never
 * above it. Edit is offered only where the page can open the editor.
 */
export function LearningDocumentActions({ text, onEdit }: { text: string; onEdit?: (() => void) | undefined }) {
  const [message, setMessage] = useState("");

  const copy = async () => {
    try {
      if (!navigator.clipboard?.writeText) throw new Error();
      await navigator.clipboard.writeText(text);
      setMessage("Document copied.");
    } catch {
      setMessage("This browser could not copy the document. Try printing or saving it as a PDF instead.");
    }
  };

  return (
    <div className="learning-document-actions">
      <div>
        {onEdit && <button type="button" onClick={onEdit}>Edit</button>}
        <button type="button" onClick={() => { void copy(); }}>Copy document</button>
        <button type="button" onClick={() => window.print()}>Print or save PDF</button>
      </div>
      <p role="status" aria-live="polite" aria-atomic="true">{message}</p>
    </div>
  );
}

export function learningRecordPlainText(title: string, values: AttendeeLearningRecordValues): string {
  return documentText(title, [
    ["The idea to carry", values.centralIdea],
    ["Core ideas", values.mainPoints],
    ["Routines and steps", values.frameworks],
    ["Examples worth remembering", values.examples],
    ["Ways to use this in class", values.practicalStrategies],
    ["Resources mentioned", values.resources],
    ["What stood out to me", values.whatStoodOut],
    ["Questions I’m carrying", values.questions],
    ["Try next", values.nextActions],
  ]);
}

export function conferenceReviewPlainText(eventName: string, values: AttendeeConferenceReviewValues): string {
  return documentText(`${eventName}\nMy Convention Wrap-Up`, [
    ["Main idea", values.centralDirection?.text ?? ""],
    ["Ideas to keep", values.importantIdeas.map(({ text }) => text)],
    ["Connections across sessions", values.themes.map(({ text }) => text)],
    ["Changes in my thinking", values.changesInThinking.map(({ text }) => text)],
    ["Questions I still have", values.tensions.map(({ text }) => text)],
    ["What this could mean for my work", values.implications.map(({ text }) => text)],
    ["What I want to try", values.actionDirection?.text ?? ""],
  ]);
}

function documentText(title: string, sections: readonly (readonly [string, string | readonly string[]])[]): string {
  return `${title}\n\n${sections.map(([heading, content]) => {
    const values = typeof content === "string" ? content.trim() ? [content.trim()] : [] : content.filter((value) => value.trim());
    const body = values.length ? values.map((value) => `- ${value}`).join("\n") : "- Not added";
    return `${heading}\n${body}`;
  }).join("\n\n")}\n`;
}
